import React, { useEffect, useState } from 'react';
import { Form, Input, Button, Card, message, Alert, Space, InputNumber, Progress, Table, Divider, Statistic, Row, Col } from 'antd';
import { RobotOutlined, CheckCircleOutlined, CloseCircleOutlined, DollarOutlined } from '@ant-design/icons';
import api from '../../api/axios';

const AiSettingsTab = () => {
  const [form] = Form.useForm();
  const [keySet, setKeySet] = useState(false);
  const [maskedKey, setMaskedKey] = useState(null);
  const [loading, setLoading] = useState(false);
  const [usage, setUsage] = useState(null);

  const fetchData = async () => {
    try {
      const [settingsRes, usageRes] = await Promise.all([
        api.get('/settings'),
        api.get('/chat/usage'),
      ]);
      setKeySet(settingsRes.data.anthropic_api_key_set || false);
      setMaskedKey(settingsRes.data.anthropic_api_key_masked || null);
      form.setFieldsValue({ chat_monthly_budget_usd: settingsRes.data.chat_monthly_budget_usd || 0 });
      setUsage(usageRes.data);
    } catch {}
  };

  useEffect(() => {
    fetchData();
    window.addEventListener('focus', fetchData);
    return () => window.removeEventListener('focus', fetchData);
  }, []);

  const handleSave = async (values) => {
    setLoading(true);
    try {
      const payload = { chat_monthly_budget_usd: values.chat_monthly_budget_usd ?? 0 };
      if (values.anthropic_api_key) payload.anthropic_api_key = values.anthropic_api_key;
      await api.put('/settings', payload);
      message.success('Ayarlar kaydedildi.');
      form.setFieldValue('anthropic_api_key', '');
      await fetchData();
    } catch {
      message.error('Ayarlar kaydedilemedi.');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveKey = async () => {
    setLoading(true);
    try {
      await api.put('/settings', { anthropic_api_key: null });
      setKeySet(false);
      setMaskedKey(null);
      message.success('API anahtarı kaldırıldı.');
    } catch {
      message.error('İşlem başarısız.');
    } finally {
      setLoading(false);
    }
  };

  const historyColumns = [
    { title: 'Ay', dataIndex: 'month', key: 'month' },
    {
      title: 'Giriş Token',
      dataIndex: 'input_tokens',
      key: 'input_tokens',
      render: (v) => Number(v).toLocaleString('tr'),
    },
    {
      title: 'Çıkış Token',
      dataIndex: 'output_tokens',
      key: 'output_tokens',
      render: (v) => Number(v).toLocaleString('tr'),
    },
    {
      title: 'Maliyet',
      dataIndex: 'cost_usd',
      key: 'cost_usd',
      render: (v) => `$${Number(v).toFixed(4)}`,
    },
  ];

  const budgetPercent = usage?.usage_percent ?? null;
  const progressStatus = budgetPercent >= 100 ? 'exception' : budgetPercent >= 80 ? 'active' : 'normal';

  return (
    <div className="max-w-2xl space-y-6">
      {/* API Anahtarı & Bütçe */}
      <Card
        title={<Space><RobotOutlined />Yapay Zeka Asistanı Ayarları</Space>}
      >
        <Alert
          className="mb-4"
          type="info"
          showIcon
          message="Claude Haiku 4.5 kullanılıyor. Fiyatlandırma: $1.00/1M giriş token, $5.00/1M çıkış token. 0 girilirse limit uygulanmaz."
        />

        <div className="mb-4">
          <span className="font-medium">API Durumu: </span>
          {keySet ? (
            <span className="text-green-600">
              <CheckCircleOutlined className="mr-1" />
              Aktif — {maskedKey}
            </span>
          ) : (
            <span className="text-red-500">
              <CloseCircleOutlined className="mr-1" />
              Tanımlı değil
            </span>
          )}
        </div>

        <Form form={form} layout="vertical" onFinish={handleSave}>
          <Form.Item
            label={keySet ? 'Yeni API Anahtarı (değiştirmek için girin)' : 'Anthropic API Anahtarı'}
            name="anthropic_api_key"
            rules={[{ required: !keySet, message: 'API anahtarı gerekli' }]}
          >
            <Input.Password placeholder="sk-ant-..." />
          </Form.Item>

          <Form.Item
            label="Aylık Bütçe Limiti (USD)"
            name="chat_monthly_budget_usd"
            extra="Her ayın 1'inde sıfırlanır (Ocak, Şubat... takvim ayı bazlı). 0 = limit yok."
          >
            <InputNumber
              min={0}
              max={1000}
              step={5}
              precision={2}
              prefix="$"
              style={{ width: 180 }}
              placeholder="Örn: 10"
            />
          </Form.Item>

          <Space>
            <Button type="primary" htmlType="submit" loading={loading}>
              Kaydet
            </Button>
            {keySet && (
              <Button danger onClick={handleRemoveKey} loading={loading}>
                Anahtarı Kaldır
              </Button>
            )}
          </Space>
        </Form>
      </Card>

      {/* Kullanım Özeti */}
      {usage && (
        <Card title={<Space><DollarOutlined />Bu Ay Kullanım</Space>}>
          <Row gutter={24} className="mb-4">
            <Col span={8}>
              <Statistic
                title="Harcanan"
                value={`$${Number(usage.current_month_spend_usd).toFixed(4)}`}
              />
            </Col>
            <Col span={8}>
              <Statistic
                title="Aylık Limit"
                value={usage.monthly_budget_usd > 0 ? `$${usage.monthly_budget_usd}` : 'Yok'}
              />
            </Col>
            <Col span={8}>
              <Statistic
                title="Kalan"
                value={usage.remaining_usd !== null ? `$${Number(usage.remaining_usd).toFixed(4)}` : '∞'}
                valueStyle={{ color: budgetPercent >= 80 ? '#cf1322' : '#3f8600' }}
              />
            </Col>
          </Row>

          {usage.monthly_budget_usd > 0 && (
            <Progress
              percent={budgetPercent}
              status={progressStatus}
              format={(p) => `${p}%`}
            />
          )}

          {usage.monthly_history?.length > 0 && (
            <>
              <Divider>Son 6 Ay</Divider>
              <Table
                dataSource={usage.monthly_history}
                columns={historyColumns}
                rowKey="month"
                pagination={false}
                size="small"
              />
            </>
          )}
        </Card>
      )}
    </div>
  );
};

export default AiSettingsTab;
